import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { DialogGenericComponent } from '../components/dialog-generic/dialog-generic.component';
import { PassesService } from './passes.service';

/**
 * Butaca única ofrecida en la venta de abonos de taquilla. En los abonos de
 * eventos específicos que no la tienen obligatoria (`single_seat`), cuando las
 * butacas elegidas en el primer evento están libres en todos los del abono se
 * propone venderlo entero con esas mismas butacas. Lo comparten la venta de
 * un abono (`pass-sale`) y la de varios (`pass-sale-bulk`).
 */
@Injectable({
  providedIn: 'root'
})
export class SingleSeatPassOfferService {

  constructor(private dialog: MatDialog, private passesService: PassesService) { }

  /** Solo abonos de eventos específicos, sin butaca única obligatoria y con todos sus eventos con mapa. */
  canOffer(pass: any, passItems: any[]): boolean {
    return !!pass && !pass.single_seat && !pass.min_events
      && passItems.length > 1
      && passItems.every((item: any) => !!(item.event || item).use_map);
  }

  /**
   * Consulta si las butacas elegidas en el mapa de `eventId` están libres en
   * todos los eventos del abono y, si lo están, pregunta si se vende el abono
   * con ellas en todos. Devuelve el desglose si se acepta y null en cualquier
   * otro caso (no están libres, el abono no cumple los requisitos, se dice
   * que no o la consulta falla): quien llama sigue entonces evento a evento.
   */
  offerSameSeat(pass: any, eventId: number, seatIds: number[]): Observable<any | null> {
    if (seatIds.length === 0) {
      return of(null);
    }

    return this.passesService.quotePassSeats(pass.id, seatIds, eventId).pipe(
      switchMap((quote: any) => {
        const events = quote?.seats?.[0]?.lines?.length || 0;
        if (!quote || events < 2) {
          return of(null);
        }

        const many = seatIds.length > 1;
        const total = (+quote.total).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        const dialogRef = this.dialog.open(DialogGenericComponent, {
          disableClose: true,
          data: {
            title: many ? 'Butacas libres en todo el abono' : 'Butaca libre en todo el abono',
            subtitle: `${many ? 'Las butacas elegidas están libres' : 'La butaca elegida está libre'} en los ${events} eventos del abono. `
              + `¿Añadir ${many ? 'los abonos con las mismas butacas' : 'el abono con la misma butaca'} en todos? Total: ${total} €.`,
            icon: 'info',
            buttons: [
              { label: 'No, evento a evento', type: 'secondary', action: () => dialogRef.close(false) },
              { label: 'Sí, añadir', action: () => dialogRef.close(true) },
            ],
          },
        });

        return dialogRef.afterClosed().pipe(map((accepted: any) => accepted === true ? quote : null));
      }),
      catchError(() => of(null))
    );
  }
}
